import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useContext, useMemo, useState } from 'react';
import { Button, Divider, GridItem, VStack } from '@chakra-ui/react';
import { EComponentBaseType, useT } from '@progress-fe/core';
import { Dialog } from '../../base';
import { IconButton } from '../../buttons';
import { SimpleTable } from '../../tables';
import { useJsFieldFormData } from '../../../hooks';
import { tableDataUtils } from '../../../utils';
import { ComponentsLists } from '../../lists';
import { JsFormConfigContext } from '../../../contexts';
import { JsFieldInspector } from '../../jsFormBase';
/**
 * Component renders "/schemas/jsf-table-add-components" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is TTableAddComponentsFormData<T>.
 */
const TableAddComponentsJsFieldFC = (props) => {
    const [isDialog, setIsDialog] = useState(false);
    const context = useContext(JsFormConfigContext) ?? {};
    const { components, addToFavorites, removeFromFavorites, isInspector } = context;
    const { favorites = [], pureList = [], pseudoList = [] } = components ?? {};
    const { t } = useT();
    const { fieldConfig, fieldValues, onChangeFieldValues } = useJsFieldFormData({
        config: props.formData?.fieldConfig ?? null,
        values: props.formData?.fieldValues ?? null,
        watchPropsChanges: true,
        onChange: props.onChange
    });
    const handleAddRow = (componentId) => {
        if (fieldConfig) {
            const newRowValue = tableDataUtils.createEmptyItem(fieldConfig.columns);
            newRowValue[fieldConfig.componentPropertyName] = componentId;
            const newValues = [
                ...(fieldValues ?? []),
                { key: componentId, value: newRowValue }
            ];
            onChangeFieldValues(newValues);
        }
    };
    const handleRemoveRow = useCallback((deleteId) => {
        if (fieldConfig?.removable && fieldValues) {
            const newValues = tableDataUtils.removeValueAtId(fieldValues, deleteId);
            onChangeFieldValues(newValues);
        }
    }, [fieldConfig?.removable, fieldValues, onChangeFieldValues]);
    const handleRemoveRowByComponent = useCallback((componentId) => {
        if (fieldConfig?.removable && fieldValues) {
            const index = fieldValues.findIndex((fv) => fv.key === componentId);
            handleRemoveRow(index);
        }
    }, [fieldConfig?.removable, fieldValues, handleRemoveRow]);
    const columns = useMemo(() => {
        if (!fieldConfig?.columns) {
            return [];
        }
        if (!fieldConfig?.removable ||
            (fieldValues?.length && fieldValues.length <= fieldConfig?.minRowsNum)) {
            return fieldConfig.columns;
        }
        return [
            ...fieldConfig.columns,
            {
                name: '',
                width: '28px',
                sticky: true,
                render: (rowId) => (_jsx(IconButton, { icon: "Cross", isDisabled: !!props.schema.readOnly, onClick: () => handleRemoveRow(rowId) }))
            }
        ];
    }, [
        fieldConfig?.columns,
        fieldConfig?.minRowsNum,
        fieldConfig?.removable,
        fieldValues?.length,
        handleRemoveRow,
        props.schema.readOnly
    ]);
    const handleValueChange = (rowId, newValue) => {
        if (!fieldValues || !fieldConfig) {
            return;
        }
        const editedValue = { ...fieldValues[rowId], value: newValue };
        const newTableData = tableDataUtils.changeValueById(fieldValues, editedValue, rowId);
        const formattedNewTableData = newTableData.map((fv) => {
            const valueCopy = structuredClone(fv.value);
            valueCopy[fieldConfig.componentPropertyName] = fv.key.toString();
            return { ...fv, value: valueCopy };
        });
        onChangeFieldValues(formattedNewTableData);
    };
    const tabs = useMemo(() => {
        return [
            {
                type: null,
                label: t(`common.favorites`),
                components: favorites
            },
            {
                type: EComponentBaseType.PURE,
                label: t(`enum.componentBaseType.${EComponentBaseType.PURE}`),
                components: pureList
            },
            {
                type: EComponentBaseType.HYPOTHETICAL,
                label: t(`enum.componentBaseType.${EComponentBaseType.HYPOTHETICAL}`),
                components: []
            },
            {
                type: EComponentBaseType.PSEUDO,
                label: t(`enum.componentBaseType.${EComponentBaseType.PSEUDO}`),
                components: pseudoList
            }
        ];
    }, [favorites, pureList, pseudoList, t]);
    const isReadOnly = !!props.schema.readOnly;
    const selectedComponentsIds = fieldValues?.map((fv) => fv.key.toString()) ?? [];
    const mapFieldsToShowRows = useMemo(() => {
        if (!fieldConfig || !fieldValues)
            return [];
        return fieldValues.map((fv) => {
            const valueCopy = structuredClone(fv.value);
            valueCopy[fieldConfig.componentPropertyName] =
                pureList.find((p) => p.uuid === fv.key)?.name ?? fv.key.toString();
            return { ...fv, value: valueCopy };
        });
    }, [fieldConfig, fieldValues, pureList]);
    return (_jsx(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: _jsxs(VStack, { "data-testid": "TableAddComponentsJsField-test", "data-name": props.name, w: "100%", gap: "8px", pb: "2px", children: [_jsx(SimpleTable, { columns: columns, rows: mapFieldsToShowRows, isReadOnly: isReadOnly, noItemsTitle: fieldConfig?.noRowsText, onValueChange: handleValueChange }), !isReadOnly && (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsx(Button, { variant: "ghost", size: "smSquare", w: "100%", onClick: () => setIsDialog(true), children: fieldConfig?.addText })] })), isDialog && (_jsx(Dialog, { title: t('components.add'), onClose: () => setIsDialog(false), children: _jsx(GridItem, { pt: "8px", children: _jsx(ComponentsLists, { tabs: tabs, tableSx: { height: 500 }, selectedComponentsIds: selectedComponentsIds, onAddComponent: handleAddRow, onDeleteComponent: handleRemoveRowByComponent, onAddToFavorite: (item) => addToFavorites?.([item]), onDeleteFromFavorite: (item) => removeFromFavorites?.([item]), onClose: () => setIsDialog(false) }) }) }))] }) }));
};
export const TableAddComponentsJsField = memo(TableAddComponentsJsFieldFC);
//# sourceMappingURL=TableAddComponentsJsField.js.map